import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axiosPublic, { axiosPrivate } from "../API/axios";
import {
  bootstrapSession,
  endSession,
  getSessionSnapshot,
  installAuthInterceptors,
  installStorageSync,
  replaceTokens,
  SESSION_END_REASON,
  SESSION_EVENT,
  startSession,
  subscribeToSession,
} from "../auth/authClient";
import { can } from "../auth/permissions";
import {
  buildAuthSessionState,
  getStoredPersistPreference,
} from "../utils/session";

export const AUTH_STATUS = Object.freeze({
  BOOTSTRAPPING: "bootstrapping",
  AUTHENTICATED: "authenticated",
  ANONYMOUS: "anonymous",
});

const AuthContext = createContext({});

installAuthInterceptors();
installStorageSync();

const hasSession = (session) =>
  Boolean(session?.accessToken || session?.refreshToken);

// What the login page tells the operator after an involuntary sign-out.
const toSessionEndReason = (reason) =>
  reason === SESSION_END_REASON.ACCOUNT_SUSPENDED ? "suspended" : "expired";

// The admin panel identifies itself at sign-in (DESIGN §8): admin sessions
// have no absolute duration limit, agent-portal sessions do.
const LOGIN_CLIENT = "admin_panel";

export const AuthProvider = ({ children, skipBootstrap = false }) => {
  const initialSession = getSessionSnapshot();
  const [auth, setAuthState] = useState(initialSession);
  const [status, setStatus] = useState(() =>
    skipBootstrap
      ? hasSession(initialSession)
        ? AUTH_STATUS.AUTHENTICATED
        : AUTH_STATUS.ANONYMOUS
      : hasSession(initialSession)
        ? AUTH_STATUS.BOOTSTRAPPING
        : AUTH_STATUS.ANONYMOUS,
  );
  const [sessionEndReason, setSessionEndReason] = useState("");
  const [persist, setPersist] = useState(getStoredPersistPreference);
  const [user, setUser] = useState(null);
  const [profileStatus, setProfileStatus] = useState("idle");
  const profileRequestRef = useRef(0);

  // Mirror the module-level session into React state.
  useEffect(() => {
    const unsubscribe = subscribeToSession((event, payload) => {
      if (event === SESSION_EVENT.AUTHENTICATED) {
        // Invalidate any /user/me response still in flight for a previous account.
        profileRequestRef.current += 1;
        setSessionEndReason("");
        setUser(null);
        setProfileStatus("idle");
        setAuthState(payload);
        setStatus(AUTH_STATUS.AUTHENTICATED);
        return;
      }
      if (event === SESSION_EVENT.REFRESHED) {
        setAuthState(payload);
        return;
      }
      if (event === SESSION_EVENT.EXPIRED || event === SESSION_EVENT.LOGGED_OUT) {
        profileRequestRef.current += 1;
        setUser(null);
        setProfileStatus("idle");
        setAuthState(getSessionSnapshot());
        setSessionEndReason(event === SESSION_EVENT.EXPIRED ? toSessionEndReason(payload?.reason) : "logged_out");
        setStatus(AUTH_STATUS.ANONYMOUS);
      }
    });
    return unsubscribe;
  }, []);

  // Restore the session once on mount, before any protected route renders.
  useEffect(() => {
    if (skipBootstrap) {
      return undefined;
    }
    let active = true;
    bootstrapSession().then((result) => {
      if (!active) return;
      setAuthState(getSessionSnapshot());
      if (result.authenticated) {
        setStatus(AUTH_STATUS.AUTHENTICATED);
      } else {
        setStatus(AUTH_STATUS.ANONYMOUS);
        if (result.expired) setSessionEndReason(toSessionEndReason(result.reason));
      }
    });
    return () => {
      active = false;
    };
  }, [skipBootstrap]);

  useEffect(() => {
    try {
      localStorage.setItem("persist", JSON.stringify(persist));
    } catch {}
  }, [persist]);

  const refreshProfile = useCallback(async () => {
    const requestId = ++profileRequestRef.current;
    setProfileStatus("loading");
    try {
      const response = await axiosPrivate.get("/user/me");
      if (requestId !== profileRequestRef.current) return null;
      const nextUser = response?.data?.data?.user ?? null;
      setUser(nextUser);
      setProfileStatus("ready");
      return nextUser;
    } catch (error) {
      if (requestId !== profileRequestRef.current) return null;
      setProfileStatus("error");
      return null;
    }
  }, []);

  // Load /user/me once per authenticated session (shared by header, top-ups…).
  useEffect(() => {
    if (status !== AUTH_STATUS.AUTHENTICATED) {
      return;
    }
    if (profileStatus === "idle") {
      refreshProfile();
    }
  }, [status, profileStatus, refreshProfile]);

  const login = useCallback(
    async (credentials) => {
      const response = await axiosPublic.post(
        "/auth/login",
        { ...credentials, client: LOGIN_CLIENT },
        { headers: { "Content-Type": "application/json" } },
      );
      const sessionState = buildAuthSessionState(response?.data?.data ?? {});
      startSession(sessionState, { persist });
      return sessionState;
    },
    [persist],
  );

  const logout = useCallback(async () => {
    await endSession({ revoke: true });
  }, []);

  // Compatibility for the password page, which receives a new token pair.
  const setAuth = useCallback(
    (updater) => {
      const next = typeof updater === "function" ? updater(getSessionSnapshot()) : updater;
      if (next?.accessToken && next?.refreshToken) {
        replaceTokens(
          { accessToken: next.accessToken, refreshToken: next.refreshToken },
          { persist },
        );
        return;
      }
      if (!next || !Object.keys(next).length) {
        endSession({ revoke: false });
      }
    },
    [persist],
  );

  const value = useMemo(
    () => ({
      auth,
      status,
      isBootstrapping: status === AUTH_STATUS.BOOTSTRAPPING,
      isAuthenticated: status === AUTH_STATUS.AUTHENTICATED,
      role: auth?.role || "",
      sessionEndReason,
      user,
      profileStatus,
      refreshProfile,
      can: (capability) => can(auth?.role, capability),
      login,
      logout,
      setAuth,
      persist,
      setPersist,
    }),
    [auth, status, sessionEndReason, user, profileStatus, refreshProfile, login, logout, setAuth, persist],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
